import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, HostBinding, HostListener, OnDestroy, OnInit, inject, input, viewChild, output } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Subject, zip } from "rxjs";
import { timeout } from "rxjs/operators";

import { MediaFilterTypeEnum } from "../../core-ui.enums";
import { IMediaFilter } from "../../models";
import {
  GenericErrorHandler,
  KeyboardService,
  KeyCodes,
  MediaEffectsService,
} from "../../services";
import { debug } from "../../services/utils";

@Component({
  selector: "app-background-effect",
  templateUrl: "./background-effect.component.html",
  styleUrls: ["./background-effect.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BackgroundEffectComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private effects = inject(MediaEffectsService);
  private cd = inject(ChangeDetectorRef);
  private errorHandler = inject(GenericErrorHandler);
  host = inject(ElementRef);
  private destroyRef = inject(DestroyRef);

  readonly effect = input<IMediaFilter>(undefined);
  readonly active = input<boolean>(undefined);

  readonly selected = output<IMediaFilter>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  readonly error = output<IMediaFilter>();

  readonly imageRef = viewChild<ElementRef<HTMLImageElement>>("image");

  typeNone = MediaFilterTypeEnum.none;
  typeBlur = MediaFilterTypeEnum.backgroundBlur;
  typeImage = MediaFilterTypeEnum.backgroundImage;

  url: string;

  private viewInitSubject: Subject<void> = new Subject();
  private urlLoaded: Subject<string> = new Subject();

  ngOnInit(): void {
    // wait for all observables to complete in order to apply existing effect
    // try to fix bug when effect was applied before image url was loaded
    zip(
      this.viewInitSubject.asObservable(),
      this.urlLoaded.asObservable()
    ).subscribe(() => {
      if (this.active()) {
        this.effects.setFilter(this.effect(), this.imageRef()?.nativeElement);
      }
    });

    const effect = this.effect();
    if (effect.type === MediaFilterTypeEnum.backgroundImage) {
      effect.url?.pipe(timeout({ first: 10000 }), takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (url) => {
            debug("bkg-effect: url loaded ", url, this.effect());

            this.url = url;
            this.cd.detectChanges();

            this.urlLoaded.next(url);
          },
          error: (error) => {
            const effectValue = this.effect();
            debug("bkg-effect: url timed out ", this.url, effectValue);
            this.errorHandler.handleError(error);

            if (!this.url) {
              this.error.emit(effectValue);
            }
          },
        });
    } else {
      this.urlLoaded.next(null);
    }
  }

  ngAfterViewInit() {
    this.viewInitSubject.next();
  }

  ngOnDestroy() {
    debug("bkg-effect: destroyed", this.effect());
  }

  failed() {
    const effect = this.effect();
    debug("bkg-effect: failed", effect);
    this.error.emit(effect);
  }

  @HostBinding("class") get class() {
    return {
      active: this.active(),
    };
  }

  @HostListener("keyup", ["$event"])
  onSpaceKey(event: KeyboardEvent) {
    if (
      KeyCodes.Space === event.code ||
      KeyCodes.Enter === event.code ||
      KeyCodes.NumpadEnter === event.code
    ) {
      this.click();
    }
  }

  @HostListener("click")
  click() {
    const effect = this.effect();
    if (
      (effect.type === this.typeImage && this.url) ||
      effect.type !== this.typeImage
    ) {
      this.effects.setFilter(effect, this.imageRef()?.nativeElement);
      this.selected.emit(effect);
    }
  }
}
